import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import LegalDocumentView, { countCounselNotes, publicLegalBlocks } from '../Legal/LegalDocumentView';
import {
  deleteDraft,
  getVersion,
  publishVersion,
  updateDraft,
} from '../../agents/legalDocumentsAgent';
import { acceptanceLabel, formatShortDate } from './legalDocumentsUtils';
import { FORMAT_ACTIONS, applyEditToTextarea, formatMarkdown } from './legalMarkdownFormatting';
import styles from './LegalDocumentsManager.module.css';

const EDITABLE_FIELDS = ['version', 'title', 'change_summary', 'counsel_approved', 'body_markdown'];

const TOOLBAR_GROUPS = [
  ['heading', 'subheading'],
  ['bold', 'italic', 'link'],
  ['bullets', 'numbers', 'callout'],
  ['counselNote'],
];

const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '');

/** Side-by-side editing only fits on wide screens; start there when it does. */
const defaultDraftMode = () => {
  try {
    return window.matchMedia('(min-width: 1200px)').matches ? 'split' : 'write';
  } catch {
    return 'write';
  }
};

/**
 * FormattingToolbar — buttons that insert Markdown so editors don't need to
 * know the syntax.
 */
function FormattingToolbar({ onFormat, controls, disabled }) {
  return (
    <div className={styles.formatBar} role="toolbar" aria-label="Formatting" aria-controls={controls}>
      {TOOLBAR_GROUPS.map((group) => (
        <div key={group.join('-')} className={styles.formatGroup}>
          {group.map((action) => {
            const meta = FORMAT_ACTIONS[action];
            const shortcut = meta.shortcut ? ` (${IS_MAC ? '⌘' : 'Ctrl+'}${meta.shortcut.toUpperCase()})` : '';
            return (
              <button
                key={action}
                type="button"
                className={`${styles.formatButton} ${styles[`format_${action}`] || ''}`}
                aria-label={meta.label}
                title={`${meta.label}${shortcut}`}
                // Keep the textarea's selection: don't take focus on mousedown.
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => onFormat(action)}
                disabled={disabled}
              >
                {meta.short}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

const toForm = (record) => ({
  version: record.version || '',
  title: record.title || '',
  change_summary: record.change_summary || '',
  counsel_approved: Boolean(record.counsel_approved),
  body_markdown: record.body_markdown || '',
});

/**
 * ConfirmDialog — small confirm step on top of the shared Modal.
 */
export function ConfirmDialog({
  open,
  title,
  children,
  confirmLabel,
  confirmVariant = 'primary',
  busy = false,
  error = null,
  onConfirm,
  onCancel,
}) {
  const bodyId = useId();
  return (
    <Modal
      open={open}
      onClose={busy ? () => {} : onCancel}
      title={title}
      size="sm"
      describedBy={bodyId}
      footer={(
        <div className={styles.dialogActions}>
          <Button variant="ghost" className={styles.btn} onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button variant={confirmVariant} className={styles.btn} onClick={onConfirm} disabled={busy}>
            {busy ? 'Working…' : confirmLabel}
          </Button>
        </div>
      )}
    >
      <div id={bodyId} className={styles.dialogBody}>
        {children}
        {error && (
          <p className={styles.inlineError} role="alert">{error}</p>
        )}
      </div>
    </Modal>
  );
}

/**
 * LegalDocumentEditor — edit a draft legal document version, or view a
 * published one read-only. Drafts can be saved, deleted, and published.
 *
 * @param {object} props
 * @param {number|string} props.versionId - Version to open.
 * @param {{key: string, path: string, title: string}} props.document - Registry entry.
 * @param {() => void} props.onBack - Return to the overview.
 * @param {() => void} props.onChanged - A save landed; refresh the overview list.
 * @param {(summary: object) => void} props.onPublished - Draft went live.
 * @param {(version: string) => void} props.onDeleted - Draft deleted.
 */
export default function LegalDocumentEditor({
  versionId,
  document: legalDoc,
  onBack,
  onChanged,
  onPublished,
  onDeleted,
}) {
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [attempt, setAttempt] = useState(0);

  const [form, setForm] = useState(null);
  const [saved, setSaved] = useState(null);
  const [mode, setMode] = useState('write');
  const [busy, setBusy] = useState(null); // 'save' | 'delete' | 'publish'
  const [actionError, setActionError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [confirm, setConfirm] = useState(null); // 'publish' | 'delete' | 'discard'
  const [confirmError, setConfirmError] = useState(null);

  const fieldId = useId();
  const bodyRef = useRef(null);
  const pendingSelection = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    getVersion(versionId)
      .then((data) => {
        if (cancelled) return;
        setRecord(data);
        setForm(toForm(data));
        setSaved(toForm(data));
        setMode(data.status === 'published' ? 'preview' : defaultDraftMode());
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err?.message || 'Failed to load this version');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [versionId, attempt]);

  const isDraft = record?.status === 'draft';
  const dirtyFields = useMemo(
    () => (form && saved ? EDITABLE_FIELDS.filter((field) => form[field] !== saved[field]) : []),
    [form, saved],
  );
  const dirty = isDraft && dirtyFields.length > 0;

  // Closing the tab with unsaved edits asks first.
  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (event) => {
      event.preventDefault();
      // eslint-disable-next-line no-param-reassign
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const previewBlocks = useMemo(() => publicLegalBlocks(form?.body_markdown), [form]);
  const hiddenNotes = useMemo(() => countCounselNotes(form?.body_markdown), [form]);

  const setField = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    setNotice(null);
  };

  const versionMissing = isDraft && !String(form?.version || '').trim();

  // Restore the selection after a formatting edit that went through state.
  useLayoutEffect(() => {
    const selection = pendingSelection.current;
    const textarea = bodyRef.current;
    if (!selection || !textarea) return;
    pendingSelection.current = null;
    textarea.focus();
    textarea.setSelectionRange(selection[0], selection[1]);
  });

  const handleFormat = (action) => {
    const textarea = bodyRef.current;
    if (!textarea) return;
    const change = formatMarkdown(textarea.value, textarea.selectionStart, textarea.selectionEnd, action);
    const fallbackValue = applyEditToTextarea(textarea, change);
    if (fallbackValue !== null) {
      pendingSelection.current = [change.selectionStart, change.selectionEnd];
      setForm((prev) => ({ ...prev, body_markdown: fallbackValue }));
    }
    setNotice(null);
  };

  const handleBodyKeyDown = (event) => {
    if (!(event.ctrlKey || event.metaKey) || event.altKey || event.shiftKey) return;
    const action = Object.keys(FORMAT_ACTIONS).find(
      (key) => FORMAT_ACTIONS[key].shortcut === event.key.toLowerCase(),
    );
    if (!action) return;
    event.preventDefault();
    handleFormat(action);
  };

  /** PATCH only the changed fields. Returns false when the save failed. */
  const save = useCallback(async () => {
    if (!dirtyFields.length) return true;
    const patch = {};
    dirtyFields.forEach((field) => {
      patch[field] = field === 'version' || field === 'title' ? form[field].trim() : form[field];
    });
    const updated = await updateDraft(versionId, patch);
    const next = { ...form, ...patch };
    setSaved(next);
    setForm(next);
    setRecord((prev) => ({ ...prev, ...(updated || {}), ...patch }));
    onChanged();
    return true;
  }, [dirtyFields, form, versionId, onChanged]);

  const handleSave = async () => {
    setBusy('save');
    setActionError(null);
    setNotice(null);
    try {
      await save();
      setNotice('Draft saved.');
    } catch (err) {
      setActionError(err?.message || 'Failed to save the draft');
    } finally {
      setBusy(null);
    }
  };

  const openConfirm = (kind) => {
    setConfirmError(null);
    setConfirm(kind);
  };

  const handlePublish = async () => {
    setBusy('publish');
    setConfirmError(null);
    try {
      if (dirty) await save();
      const summary = await publishVersion(versionId);
      setConfirm(null);
      onPublished(summary || { ...record, ...form, status: 'published' });
    } catch (err) {
      setConfirmError(err?.message || 'Failed to publish');
    } finally {
      setBusy(null);
    }
  };

  const handleDelete = async () => {
    setBusy('delete');
    setConfirmError(null);
    try {
      await deleteDraft(versionId);
      setConfirm(null);
      onDeleted(saved?.version || record?.version);
    } catch (err) {
      setConfirmError(err?.message || 'Failed to delete the draft');
    } finally {
      setBusy(null);
    }
  };

  const handleBack = () => {
    if (dirty) {
      openConfirm('discard');
    } else {
      onBack();
    }
  };

  const backButton = (
    <button type="button" className={styles.backButton} onClick={handleBack}>
      <span aria-hidden="true">←</span> All legal documents
    </button>
  );

  if (loading) {
    return (
      <div className={styles.editor}>
        {backButton}
        <p className={styles.loading} role="status">Loading version…</p>
      </div>
    );
  }

  if (loadError || !record) {
    return (
      <div className={styles.editor}>
        {backButton}
        <div className={styles.errorBanner} role="alert">
          <span>{loadError || 'Failed to load this version'}</span>
          <Button variant="secondary" size="sm" className={styles.btn} onClick={() => setAttempt((n) => n + 1)}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const noteLabel = `${hiddenNotes} counsel ${hiddenNotes === 1 ? 'note is' : 'notes are'} hidden from the public page.`;

  return (
    <div className={styles.editor} data-testid="legal-editor">
      {backButton}

      <header className={styles.editorHeader}>
        <div>
          <p className={styles.eyebrow}>
            {legalDoc.title}
            <span className={styles.pathChip}>{legalDoc.path}</span>
          </p>
          <h2 className={styles.editorTitle}>
            {isDraft ? 'Edit draft' : 'Published version'}{' '}
            <span className={styles.versionTag}>{record.version}</span>
          </h2>
        </div>
        <span className={`${styles.statusPill} ${isDraft ? styles.statusDraft : styles.statusLive}`}>
          {isDraft ? 'Draft' : record.is_current ? 'Live' : 'Superseded'}
        </span>
      </header>

      {!isDraft && (
        <div className={styles.readOnlyNote} role="note">
          <p>
            Published versions can&rsquo;t be edited. To change the {legalDoc.title}, start a new draft
            from the overview.
          </p>
          <dl className={styles.facts}>
            <div><dt>Published</dt><dd>{formatShortDate(record.published_at)}{record.published_by_email ? ` by ${record.published_by_email}` : ''}</dd></div>
            <div><dt>Counsel approved</dt><dd>{record.counsel_approved ? 'Yes' : 'No'}</dd></div>
            <div><dt>Accepted by</dt><dd>{acceptanceLabel(record.acceptance_count)}</dd></div>
            {record.change_summary && <div><dt>Change summary</dt><dd>{record.change_summary}</dd></div>}
          </dl>
        </div>
      )}

      {isDraft && (
        <div className={styles.fieldGrid}>
          <label className={styles.field} htmlFor={`${fieldId}-version`}>
            <span className={styles.label}>Version</span>
            <input
              id={`${fieldId}-version`}
              className={styles.input}
              value={form.version}
              onChange={setField('version')}
              maxLength={50}
              required
              aria-invalid={versionMissing || undefined}
            />
          </label>
          <label className={styles.field} htmlFor={`${fieldId}-title`}>
            <span className={styles.label}>Title</span>
            <input
              id={`${fieldId}-title`}
              className={styles.input}
              value={form.title}
              onChange={setField('title')}
              maxLength={200}
            />
          </label>
          <label className={`${styles.field} ${styles.fieldWide}`} htmlFor={`${fieldId}-summary`}>
            <span className={styles.label}>Change summary <span className={styles.optional}>(internal)</span></span>
            <textarea
              id={`${fieldId}-summary`}
              className={`${styles.input} ${styles.summaryInput}`}
              value={form.change_summary}
              onChange={setField('change_summary')}
              rows={2}
              placeholder="What changed and why, e.g. “Added the Stripe processor section after counsel review.”"
            />
          </label>
          <div className={`${styles.field} ${styles.fieldWide}`}>
            <label className={styles.checkboxRow} htmlFor={`${fieldId}-approved`}>
              <input
                id={`${fieldId}-approved`}
                type="checkbox"
                className={styles.checkbox}
                checked={form.counsel_approved}
                onChange={setField('counsel_approved')}
                aria-describedby={`${fieldId}-approved-help`}
              />
              <span className={styles.checkboxLabel}>Counsel approved</span>
            </label>
            <p id={`${fieldId}-approved-help`} className={styles.help}>
              Tick only once an attorney has signed off on this exact text. While unchecked, the public
              page shows the &ldquo;DRAFT — needs counsel review&rdquo; badge and is hidden from search engines.
            </p>
          </div>
        </div>
      )}

      <section className={styles.bodyPanel} aria-label="Document text">
        <div className={styles.bodyToolbar}>
          {isDraft ? (
            <div className={styles.segmented} role="group" aria-label="Editor view">
              {[
                ['write', 'Write'],
                ['split', 'Side by side'],
                ['preview', 'Preview'],
              ].map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  className={`${styles.segment} ${value === 'split' ? styles.segmentSplit : ''} ${mode === value ? styles.segmentActive : ''}`}
                  aria-pressed={mode === value}
                  onClick={() => setMode(value)}
                >
                  {label}
                </button>
              ))}
            </div>
          ) : (
            <span className={styles.toolbarLabel}>Preview — exactly as {legalDoc.path} showed it</span>
          )}
          <span className={styles.noteCount} data-testid="legal-hidden-notes">
            {noteLabel}
          </span>
        </div>

        {isDraft && mode !== 'preview' ? (
          <div className={`${styles.editArea} ${mode === 'split' ? styles.editAreaSplit : ''}`}>
            <div className={styles.writePane}>
              <FormattingToolbar onFormat={handleFormat} controls={`${fieldId}-body`} disabled={Boolean(busy)} />
              <label className={styles.visuallyHidden} htmlFor={`${fieldId}-body`}>Document Markdown</label>
              <textarea
                ref={bodyRef}
                id={`${fieldId}-body`}
                className={styles.markdownInput}
                value={form.body_markdown}
                onChange={setField('body_markdown')}
                onKeyDown={handleBodyKeyDown}
                spellCheck
                aria-describedby={`${fieldId}-body-help`}
              />
              <p id={`${fieldId}-body-help`} className={styles.help}>
                Use the toolbar, or type Markdown: <code>##</code> section heading, <code>-</code> bullet,{' '}
                <code>**bold**</code>, <code>[text](https://…)</code>. A <strong>Counsel note</strong> stays
                here for the reviewing attorney and never appears publicly.
              </p>
            </div>
            {mode === 'split' && (
              <div className={styles.previewPane}>
                <p className={styles.previewLabel} aria-hidden="true">Live preview</p>
                <div className={styles.previewFrame} data-testid="legal-preview" role="region" aria-label="Live preview">
                  <LegalDocumentView
                    blocks={previewBlocks}
                    fallbackTitle={form.title || legalDoc.title}
                    version={form.version}
                    publishedAt={record.published_at || null}
                    counselApproved={form.counsel_approved}
                  />
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className={styles.previewFrame} data-testid="legal-preview">
            <LegalDocumentView
              blocks={previewBlocks}
              fallbackTitle={form.title || legalDoc.title}
              version={form.version}
              publishedAt={record.published_at || null}
              counselApproved={form.counsel_approved}
            />
          </div>
        )}
      </section>

      {actionError && (
        <p className={styles.inlineError} role="alert">{actionError}</p>
      )}

      {isDraft && (
        <div className={styles.actionBar}>
          <span className={styles.saveState} role="status">
            {dirty ? 'Unsaved changes' : notice || 'All changes saved'}
          </span>
          <div className={styles.actionButtons}>
            <Button
              variant="ghost"
              className={`${styles.btn} ${styles.dangerGhost}`}
              onClick={() => openConfirm('delete')}
              disabled={Boolean(busy)}
            >
              Delete draft
            </Button>
            <Button
              variant="secondary"
              className={styles.btn}
              onClick={handleSave}
              disabled={Boolean(busy) || !dirty || versionMissing}
            >
              {busy === 'save' ? 'Saving…' : 'Save'}
            </Button>
            <Button
              variant="primary"
              className={styles.btn}
              onClick={() => openConfirm('publish')}
              disabled={Boolean(busy) || versionMissing}
            >
              Publish…
            </Button>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirm === 'publish'}
        title={`Publish ${legalDoc.title} ${form.version.trim()}?`}
        confirmLabel={dirty ? 'Save and publish' : 'Publish'}
        busy={busy === 'publish'}
        error={confirmError}
        onConfirm={handlePublish}
        onCancel={() => setConfirm(null)}
      >
        <p>
          This goes live on {legalDoc.path} immediately and everyone who signs up from now on accepts
          version {form.version.trim()}. Published versions can&rsquo;t be edited.
        </p>
        {dirty && <p className={styles.dialogWarning}>Your unsaved changes will be saved first.</p>}
        {!form.counsel_approved && (
          <p className={styles.dialogWarning}>
            Counsel approval isn&rsquo;t ticked, so the page will keep the DRAFT badge and stay hidden from
            search engines.
          </p>
        )}
      </ConfirmDialog>

      <ConfirmDialog
        open={confirm === 'delete'}
        title={`Delete draft ${saved?.version || record.version}?`}
        confirmLabel="Delete draft"
        confirmVariant="danger"
        busy={busy === 'delete'}
        error={confirmError}
        onConfirm={handleDelete}
        onCancel={() => setConfirm(null)}
      >
        <p>The draft and any unsaved edits are removed. The live version isn&rsquo;t affected.</p>
      </ConfirmDialog>

      <ConfirmDialog
        open={confirm === 'discard'}
        title="Discard unsaved changes?"
        confirmLabel="Discard changes"
        confirmVariant="danger"
        onConfirm={() => {
          setConfirm(null);
          onBack();
        }}
        onCancel={() => setConfirm(null)}
      >
        <p>Your edits to this draft since the last save will be lost.</p>
      </ConfirmDialog>
    </div>
  );
}
